import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./RequestTimelineChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import RequestsOverviewService from "../RequestsOverviewService";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardCategoryAxisTicks,
    buildMonitorDashboardResponsiveChartOptions,
    buildMonitorDashboardValueAxisTicks,
    getMonitorDashboardFontFamily,
    reverseMonitorDashboardTimelineSeries,
    getMonitorDashboardChartColor,
    MONITOR_CHART_GRID_COLOR,
    buildMonitorDashboardRequestsTimelineInnerStyle,
    buildMonitorDashboardRequestsTimelineLayoutPadding,
    buildMonitorDashboardRequestsTimelineScrollStyle,
    buildMonitorDashboardRequestsTimelineXAxisGridLines,
    scheduleMonitorDashboardChartResize,
} from "../../../chart/monitorDashboardChartKit";

const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

@WithRender
@Component({
  diProvide: [RequestsOverviewService],
})
export default class RequestTimelineChart extends Vue {
  @Inject(RequestsOverviewService)
  public requestsOverviewService!: RequestsOverviewService;

  @Prop({ required: true }) public filter!: any;

  private chart: any = null;
  private canvasId: string = "request-timeline-chart-canvas";
  public hasData: boolean = true;
  public isLoading: boolean = false;
  private barCount: number = 0;
  private scrollClientWidth: number = 0;
  private chartHeightPx: number = 0;
  private requestVersion: number = 0;

  public get scrollContainerStyle(): Record<string, string> {
    return buildMonitorDashboardRequestsTimelineScrollStyle(this.barCount);
  }

  public get innerStyle(): Record<string, string> {
    return buildMonitorDashboardRequestsTimelineInnerStyle(
      this.barCount,
      this.scrollClientWidth,
      this.chartHeightPx,
    );
  }

  public async refresh(): Promise<void> {
    if (!this.filter) return;

    this.isLoading = true;
    const currentRequestVersion = ++this.requestVersion;

    const payload = {
      dateFrom: this.filter.dateFrom,
      dateTo: this.filter.dateTo,
      tenantId: this.filter.tenant,
      providerId: this.filter.providerId,
      customerType: this.filter.customerType,
      requestType: this.filter.requestType,
    };

    const resp = await this.requestsOverviewService.GetRequestTimeline(payload);
    if (currentRequestVersion !== this.requestVersion) {
      return;
    }

    this.isLoading = false;

    if (!resp || !resp.result || !Array.isArray(resp.result)) {
      this.hasData = false;
      this.barCount = 0;
      this.destroyChart();
      return;
    }

    const rows = resp.result as Array<{ year: number; month: number; count: number }>;
    this.barCount = rows.length;

    let hasAnyNonZero = false;
    for (let i = 0; i < rows.length; i++) {
      if (Number(rows[i].count) !== 0) {
        hasAnyNonZero = true;
        break;
      }
    }
    this.hasData = hasAnyNonZero;

    await this.$nextTick();
    if (!hasAnyNonZero) {
      this.destroyChart();
      return;
    }
    await this.syncTimelineDimensions();
    await this.$nextTick();
    this.renderChart(rows);
  }

  public onTitleClick(): void {
    this.navigateToRequestTimelineDetails();
  }

  private navigateToRequestTimelineDetails(): void {
    const f = this.filter;
    const overviewPayload = {
      dateFrom: f.dateFrom,
      dateTo: f.dateTo,
      tenantId: f.tenant,
      providerId: f.providerId,
      customerType: f.customerType || 0,
      requestType: f.requestType || 0,
    };

    try {
      sessionStorage.setItem(
        "monitorDashboard.overviewFilter",
        JSON.stringify(overviewPayload),
      );
    } catch (e) {
    }

    this.$router.push({ name: "RequestTimelineDetails" });
  }

  private onChartClick(): void {
    this.navigateToRequestTimelineDetails();
  }

  public beforeDestroy(): void {
    this.destroyChart();
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  private async syncTimelineDimensions(): Promise<void> {
    await this.$nextTick();
    await new Promise<void>(function (resolve) {
      window.requestAnimationFrame(function () {
        resolve();
      });
    });
    const scroll = this.$refs.timelineScroll as HTMLElement;
    const stage = this.$refs.timelineStage as HTMLElement;
    if (scroll && scroll.clientHeight > 0) {
      this.chartHeightPx = scroll.clientHeight;
    } else if (stage && stage.clientHeight > 0) {
      this.chartHeightPx = stage.clientHeight;
    } else {
      this.chartHeightPx = 0;
    }
    this.scrollClientWidth = scroll && scroll.clientWidth > 0 ? scroll.clientWidth : 0;
  }

  private formatMonthLabel(year: number, month: number): string {
    const monthIndex = month >= 1 && month <= 12 ? month - 1 : 0;
    const shortYear = year >= 2000 ? String(year).slice(-2) : String(year);
    return MONTH_NAMES[monthIndex] + " " + shortYear;
  }

  private renderChart(rows: Array<{ year: number; month: number; count: number }>): void {
    const labels: string[] = [];
    const data: number[] = [];

    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      labels.push(this.formatMonthLabel(r.year, r.month));
      data.push(Number(r.count));
    }

    reverseMonitorDashboardTimelineSeries(labels, data);

    this.destroyChart();

    const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
    if (!el) return;

    const ctx = el.getContext("2d") as any;
    const self = this;
    const lineColor = getMonitorDashboardChartColor("requestTimeline");
    const fontFamily = getMonitorDashboardFontFamily();
    const baseOpts = buildMonitorDashboardResponsiveChartOptions();

    this.chart = new Chart(ctx, {
      type: "line",
      data: {
        labels: labels,
        datasets: [
          {
            label: "",
            data: data,
            borderColor: lineColor,
            backgroundColor: lineColor,
            borderWidth: 2,
            fill: false,
            pointBackgroundColor: lineColor,
            pointBorderColor: lineColor,
            pointBorderWidth: 1,
            pointRadius: 4,
            pointHoverRadius: 6,
            lineTension: 0,
          },
        ],
      },
      plugins: [
        {
          afterDatasetsDraw: function (chartInstance: any) {
            const ctx2 = chartInstance.ctx;
            chartInstance.data.datasets.forEach(function (dataset: any, i: number) {
              const meta = chartInstance.getDatasetMeta(i);
              if (!meta.hidden) {
                meta.data.forEach(function (point: any, j: number) {
                  const value = dataset.data[j];
                  const x = point._model.x;
                  const y = point._model.y;
                  ctx2.save();
                  ctx2.textAlign = "center";
                  ctx2.textBaseline = "bottom";
                  ctx2.font = "11px " + fontFamily;
                  ctx2.fillStyle = "#6b7280";
                  ctx2.fillText(String(value), x, y - 8);
                  ctx2.restore();
                });
              }
            });
          },
        },
      ],
      options: Object.assign({}, baseOpts, {
        onClick: function () {
          self.onChartClick();
        },
        layout: {
          padding: buildMonitorDashboardRequestsTimelineLayoutPadding(),
        },
        legend: {
          display: false,
        },
        scales: {
          xAxes: [
            {
              gridLines: buildMonitorDashboardRequestsTimelineXAxisGridLines(),
              ticks: buildMonitorDashboardCategoryAxisTicks(11, fontFamily, 45),
            },
          ],
          yAxes: [
            {
              gridLines: { color: MONITOR_CHART_GRID_COLOR },
              ticks: buildMonitorDashboardValueAxisTicks(11, fontFamily, true),
            },
          ],
        },
        tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
          callbacks: {
            title: function (tooltipItems: any[]) {
              if (!tooltipItems || tooltipItems.length === 0) return "";
              const idx = tooltipItems[0].index;
              return labels[idx];
            },
            label: function (tooltipItem: any, data: any) {
              const idx = tooltipItem.index;
              const lines: string[] = [];
              let total = 0;
              for (let i = 0; i < data.datasets.length; i++) {
                const ds = data.datasets[i];
                const raw = ds && ds.data && ds.data.length > idx ? Number(ds.data[idx]) : 0;
                const value = isNaN(raw) ? 0 : raw;
                total += value;
                const dsLabel = ds && ds.label ? String(ds.label) : String(self.$t("monitorDashboard.requestsCountLabel"));
                lines.push(dsLabel + ": " + String(value));
              }
              if (data.datasets.length > 1) {
                lines.push(String(self.$t("monitorDashboard.total")) + ": " + String(total));
              }
              return lines;
            },
          },
        }),
      }),
    });

    scheduleMonitorDashboardChartResize(this.chart, this);
  }
}
